import { inject, Injectable, signal } from '@angular/core';
import { HighDemandMolecule } from '../../../shared/models/treatments/high-demand-molecule.model';
import { Pacient } from '../../../shared/models/users/pacient.model';
import { Treatment } from '../../../shared/models/treatments/treatment.model';
import { UserService } from '../user-management/user.service';
import { RestApiV2Service } from '../http/rest-api-v2.service';
import { take } from 'rxjs';
import { TreatmentUtils } from '../../../features/treatments-module/treatmentUtils';
import { PrescriptionsService } from './prescriptions.service';
import { DoctorUserInfo, NurseUserInfo } from '../../../shared/models/users/user.model';
import { DashboardInteraction, DashboardInteractionItem, InteractionType } from '../../../shared/models/treatments/dashboardInteraction.model';
import { Effect } from '../../../shared/models/treatments/effects.model';
import moment from 'moment';

export interface IpsDashboardStats {
  interactions: {
    totalCount: number;
    complianceRate: number;
    alerts: { read: number; unread: number };
    messages: { read: number; unread: number };
    prescriptions: { read: number; unread: number };
  };
  alerts: {
    total: number;
    generated: { date: string; count: number }[];
  };
  treatments: {
    active: number;
    linking: number;
    inactive: number;
  };
  highDemandMolecules: { molecule: string; count: number }[];
}

export interface DoctorUserInfoWithInteractions extends DoctorUserInfo {
  interactions: DashboardInteraction;
}

export interface IpsData {
  molecules: HighDemandMolecule[];
  pacients: {pacient: Pacient, treatments: Treatment[] }[];
  professionals: {professional: DoctorUserInfoWithInteractions, pacients: {pacient: Pacient, treatments: Treatment[] }[]}[];
}

@Injectable({
  providedIn: 'root',
})
export class IpsService {

  private readonly userService = inject(UserService);
  private readonly restApiV2 = inject(RestApiV2Service);
  readonly prescriptionsService = inject(PrescriptionsService);
  readonly treatmentUtils = TreatmentUtils;

  readonly data = signal<IpsData>({ molecules: [], pacients: [], professionals: [] });

  getMyData() {
    const user = this.userService.userData();
    const response = this.restApiV2.get<{data: { data: IpsData } }>('ips/get-my-data', { admin_id: user!.id });

    response.request$
    .pipe(take(1))
    .subscribe(response => {
      this.processData(response.data.data);
    });
  }

  processData(data: IpsData) {
    console.log(data);
    this.data.set(data);

    /* Process prescriptions */
    const groupedPrescriptions = this.treatmentUtils.getPrescriptionsWithTreatments(data.pacients);
    this.prescriptionsService.setPrescriptions(groupedPrescriptions);
  }

  processIpsDashboardStats(data: IpsData): IpsDashboardStats {
    // Procesar interacciones - sumar los valores de cada interacción de todos los profesionales
    let readAlerts = 0;
    let unreadAlerts = 0;
    let readMessages = 0;
    let unreadMessages = 0;
    let readPrescriptions = 0;
    let unreadPrescriptions = 0;

    data.professionals.forEach(prof => {
      readAlerts += prof.professional.interactions.read_alerts?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      unreadAlerts += prof.professional.interactions.unread_alerts?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      readMessages += prof.professional.interactions.read_messages?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      unreadMessages += prof.professional.interactions.unread_messages?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      readPrescriptions += prof.professional.interactions.read_prescriptions?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
      unreadPrescriptions += prof.professional.interactions.unread_prescriptions?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
    });

    const totalInteractions = readAlerts + unreadAlerts + readMessages + unreadMessages + readPrescriptions + unreadPrescriptions;
    const totalRead = readAlerts + readMessages + readPrescriptions;
    const complianceRate = totalInteractions > 0 ? (totalRead / totalInteractions) * 100 : 0;

    // Procesar efectos adversos reportados por fecha
    const alertsByDate = new Map<string, number>();
    data.pacients.forEach(p => {
      p.treatments.forEach(t => {
        t.interactions?.forEach(alert => {
          if (alert.created_at) {
            const date = moment(alert.created_at).utcOffset('-05:00').format('YYYY-MM-DD');
            alertsByDate.set(date, (alertsByDate.get(date) || 0) + 1);
          }
        });
      });
    });

    const generatedAlerts = Array.from(alertsByDate.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Procesar estados de tratamientos
    let activeTreatments = 0;
    let linkingTreatments = 0;
    let inactiveTreatments = 0;

    data.pacients.forEach(p => {
      p.treatments.forEach(t => {
        // status puede ser: 0 (inactivo), 1 (activo), 2 (en vinculación)
        if (t.status === 2) {
          linkingTreatments++;
        } else if (t.status === 1) {
          activeTreatments++;
        } else if (t.status === 0) {
          inactiveTreatments++;
        }
      });
    });

    // Procesar moléculas de alta demanda
    const highDemandMolecules = data.molecules
      .map(molecule => ({
        molecule: molecule.molecule,
        count: molecule.value,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      interactions: {
        totalCount: totalInteractions,
        complianceRate: Math.round(complianceRate * 100) / 100,
        alerts: { read: readAlerts, unread: unreadAlerts },
        messages: { read: readMessages, unread: unreadMessages },
        prescriptions: { read: readPrescriptions, unread: unreadPrescriptions },
      },
      alerts: {
        total: generatedAlerts.reduce((sum, alert) => sum + alert.count, 0),
        generated: generatedAlerts,
      },
      treatments: {
        active: activeTreatments,
        linking: linkingTreatments,
        inactive: inactiveTreatments,
      },
      highDemandMolecules: highDemandMolecules,
    };
  }

  manageIpsTreatment(treatment: Treatment, socketType: 'NEW_TREATMENT' | 'ALTER_TREATMENT' | 'REMOVE_TREATMENT') {
    this.data.update(currentData => {
      const pacientId = treatment.pacient_id;
      const pacientIndex = currentData.pacients.findIndex(p => p.pacient.id === pacientId);
      
      // REMOVE_TREATMENT y ALTER_TREATMENT se comportan igual: actualizan el tratamiento
      if (socketType === 'ALTER_TREATMENT' || socketType === 'REMOVE_TREATMENT') {
        if (pacientIndex === -1) {
          // Actualizar en professionals si no está en pacients
          return this.updateTreatmentInProfessionals(currentData, treatment);
        }
        
        const pacient = currentData.pacients[pacientIndex];
        const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
        
        if (treatmentIndex === -1) {
          return this.updateTreatmentInProfessionals(currentData, treatment);
        }
        
        const updatedPacients = currentData.pacients.map((p, idx) => 
          idx === pacientIndex 
            ? { ...p, treatments: p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t) }
            : p
        );
        
        return {
          ...currentData,
          pacients: updatedPacients,
          professionals: this.updateTreatmentInProfessionalsArray(currentData.professionals, treatment)
        };
      }
      
      // NEW_TREATMENT
      if (pacientIndex !== -1) {
        const pacient = currentData.pacients[pacientIndex];
        const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
        
        const updatedPacients = currentData.pacients.map((p, idx) => 
          idx === pacientIndex
            ? { ...p, treatments: treatmentIndex !== -1 
                ? p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t)
                : [...p.treatments, treatment]
              }
            : p
        );
        
        return {
          ...currentData,
          pacients: updatedPacients,
          professionals: this.updateTreatmentInProfessionalsArray(currentData.professionals, treatment)
        };
      }
      
      if (treatment.pacient) {
        return {
          ...currentData,
          pacients: [...currentData.pacients, { pacient: treatment.pacient, treatments: [treatment] }],
          professionals: this.updateTreatmentInProfessionalsArray(currentData.professionals, treatment)
        };
      }
      
      return this.updateTreatmentInProfessionals(currentData, treatment);
    });

    const currentData = this.data();
    const groupedPrescriptions = this.treatmentUtils.getPrescriptionsWithTreatments(currentData.pacients);
    this.prescriptionsService.setPrescriptions(groupedPrescriptions);
  }

  private updateTreatmentInProfessionals(currentData: IpsData, treatment: Treatment): IpsData {
    return {
      ...currentData,
      professionals: this.updateTreatmentInProfessionalsArray(currentData.professionals, treatment)
    };
  }

  private updateTreatmentInProfessionalsArray(
    professionals: {professional: DoctorUserInfoWithInteractions, pacients: {pacient: Pacient, treatments: Treatment[] }[]}[],
    treatment: Treatment
  ): {professional: DoctorUserInfoWithInteractions, pacients: {pacient: Pacient, treatments: Treatment[] }[]}[] {
    return professionals.map(prof => {
      const pacientIndex = prof.pacients.findIndex(p => p.pacient.id === treatment.pacient_id);
      
      if (pacientIndex === -1) {
        if (treatment.pacient) {
          return {
            ...prof,
            pacients: [...prof.pacients, { pacient: treatment.pacient, treatments: [treatment] }]
          };
        }
        return prof;
      }
      
      const pacient = prof.pacients[pacientIndex];
      const treatmentIndex = pacient.treatments.findIndex(t => t.id === treatment.id);
      
      return {
        ...prof,
        pacients: prof.pacients.map((p, idx) => 
          idx === pacientIndex
            ? { ...p, treatments: treatmentIndex !== -1 
                ? p.treatments.map((t, tIdx) => tIdx === treatmentIndex ? treatment : t)
                : [...p.treatments, treatment]
              }
            : p
        )
      };
    });
  }

  addEffectsToTreatments(effects: Effect[]) {
    this.data.update(currentData => {
      return {
        ...currentData,
        pacients: this.addEffectsToPacients(currentData.pacients, effects),
        professionals: currentData.professionals.map(prof => ({
          ...prof,
          pacients: this.addEffectsToPacients(prof.pacients, effects)
        }))
      };
    });
  }

  private addEffectsToPacients(
    pacients: {pacient: Pacient, treatments: Treatment[] }[],
    effects: Effect[]
  ): {pacient: Pacient, treatments: Treatment[] }[] {
    return pacients.map(pacient => ({
      ...pacient,
      treatments: pacient.treatments.map(treatment => {
        const treatmentEffects = effects.filter(e => e.treatment_id === treatment.id);
        if (treatmentEffects.length === 0) return treatment;
        
        const existingEffectIds = new Set(treatment.effects.map(e => e.id));
        const newEffects = treatmentEffects.filter(e => !existingEffectIds.has(e.id));
        
        return {
          ...treatment,
          effects: [...treatment.effects, ...newEffects]
        };
      })
    }));
  }

  updateDashboardInteraction(interaction: DashboardInteractionItem) {
    this.data.update(currentData => ({
      ...currentData,
      professionals: currentData.professionals.map(prof => 
        prof.professional.id === interaction.professional_id
          ? {
              ...prof,
              professional: {
                ...prof.professional,
                interactions: this.addOrUpdateInteraction(prof.professional.interactions, interaction)
              }
            }
          : prof
      )
    }));
  }

  private addOrUpdateInteraction(interactions: DashboardInteraction, item: DashboardInteractionItem): DashboardInteraction {
    const arrayKey = this.getInteractionArrayKey(item.interaction_type);
    if (!arrayKey) return interactions;

    const currentArray = interactions[arrayKey] || [];
    const existingIndex = currentArray.findIndex(i => i.id === item.id);
    
    const updatedArray = existingIndex !== -1
      ? currentArray.map((i, idx) => idx === existingIndex ? item : i)
      : [...currentArray, item];

    return {
      ...interactions,
      [arrayKey]: updatedArray
    };
  }

  private getInteractionArrayKey(type: InteractionType): keyof DashboardInteraction | null {
    switch (type) {
      case InteractionType.READ_ALERTS: return 'read_alerts';
      case InteractionType.UNREAD_ALERTS: return 'unread_alerts';
      case InteractionType.READ_MESSAGES: return 'read_messages';
      case InteractionType.UNREAD_MESSAGES: return 'unread_messages';
      case InteractionType.READ_PRESCRIPTIONS: return 'read_prescriptions';
      case InteractionType.UNREAD_PRESCRIPTIONS: return 'unread_prescriptions';
      default: return null;
    }
  }

  addProfessional(professional: DoctorUserInfo | NurseUserInfo) {

    this.data.update(currentData => {
      const professionalExists = currentData.professionals.some(
        prof => prof.professional.id === professional.id
      );

      if (professionalExists) return currentData;

      const professionalWithInteractions: DoctorUserInfoWithInteractions = {
        ...professional as DoctorUserInfo,
        interactions: {
          read_alerts: [],
          unread_alerts: [],
          read_prescriptions: [],
          unread_prescriptions: [],
          read_messages: [],
          unread_messages: []
        }
      };

      return {
        ...currentData,
        professionals: [
          ...currentData.professionals,
          {
            professional: professionalWithInteractions,
            pacients: []
          }
        ]
      };
    });
  }

  addOrUpdateMolecule(molecule: HighDemandMolecule) {
    this.data.update(currentData => {
      const moleculeIndex = currentData.molecules.findIndex(m => m.id === molecule.id);
      
      if (moleculeIndex !== -1) {
        return {
          ...currentData,
          molecules: currentData.molecules.map((m, idx) => idx === moleculeIndex ? molecule : m)
        };
      }
      
      return {
        ...currentData,
        molecules: [...currentData.molecules, molecule]
      };
    });
  }
}
